import { useQuery } from "@tanstack/react-query";
import { workflowTruckApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsWorkflowTruckI,
  WorkflowTruckI,
  ObjPostI,
  EnhancedServiceResponseI,
} from "../../interfaces";
import {
  ValidateCurrentToken,
  LogoutSystem,
  // logProcessingStep,
  // trackServerResponseDirect,
} from "../../helpers";
import { AxiosError } from "axios";
import { useRouterStore } from "../../stores";

const getWorkflowTrucks = async ({
  pagination,
  sorting,
  globalFilter,
  range,
  rangeVal,
}: PropsWorkflowTruckI): Promise<ServiceResponseI<DataI<WorkflowTruckI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    rangeVal && params.append("range", JSON.stringify(range));

    //headers: { 'Authorization': validateToken
    const { data } = await workflowTruckApi.get<
      ServiceResponseI<DataI<WorkflowTruckI>>
    >("", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    console.log(err);
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const getAllWorkflowTrucks = async (): Promise<
  ServiceResponseI<WorkflowTruckI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowTruckApi.get<
      ServiceResponseI<WorkflowTruckI[]>
    >(`/all-no-pagination`, { headers: { Authorization: validateToken } });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const generateTruckTarjaTextbyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowTruckApi.get<ServiceResponseI<any>>(
      `/tarja-text/${id}`,
      { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const downloadTruckFilesById = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowTruckApi.get<ServiceResponseI<any>>(
      `/download-full-media/${id}`,
      { headers: { Authorization: validateToken }, responseType: "blob" }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const generateTruckPdfTarjabyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowTruckApi.get<ServiceResponseI<any>>(
      `/generate-pdf-tarja/${id}`
      // { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTruckPost = async <T>(
  getForms: T
): Promise<EnhancedServiceResponseI<ObjPostI>> => {
  const startTime = Date.now();
  let offlineId: string | null = null;

  try {
    const { byPassSw } = useRouterStore.getState();

    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    // URL del endpoint - usando exactamente la misma URL que axios
    const apiPath = "api/workflow-truck";
    const url = `${process.env.REACT_APP_BACKEND!}${apiPath}`;

    // Generar un ID único para esta transacción con mejor formato
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9);
    offlineId = `wf_truck_${timestamp}_${randomId}`;

    console.log(`🔄 POST ${apiPath} - SIEMPRE usando SW con ID: ${offlineId}`);
    console.log(`📋 Datos a enviar:`, getForms);

    if (byPassSw) {
      const directResponse = await workflowTruckApi.post<
        ServiceResponseI<ObjPostI>
      >("", getForms, {
        headers: { Authorization: validateToken },
        timeout: 15000,
      });

      return directResponse.data;
    }

    // VERIFICAR que el Service Worker esté disponible - ES OBLIGATORIO
    if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
      console.error("❌ Service Worker NO está disponible - esto es crítico");
      console.warn("🔄 Intentando petición directa como último recurso...");
      try {
        const directResponse = await workflowTruckApi.post<
          ServiceResponseI<ObjPostI>
        >("", getForms, {
          headers: { Authorization: validateToken },
          timeout: 15000,
        });

        return directResponse.data;
      } catch (directError) {
        throw new Error(
          "Service Worker no disponible y petición directa falló. La aplicación necesita reiniciarse."
        );
      }
    }

    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();

      // Timeout extendido y adaptativo
      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
      const timeoutDuration = isMobile ? 60000 : 45000; // 60s para móviles, 45s para escritorio

      const timeout = setTimeout(async () => {
        console.error(
          `❌ TIMEOUT después de ${timeoutDuration}ms para ${offlineId}`
        );

        // En caso de timeout, asumir que se encoló correctamente
        const timeoutResponse: EnhancedServiceResponseI<ObjPostI> = {
          data: {
            id: parseInt(timestamp.toString().slice(-8)),
            alert:
              "Datos guardados localmente. Verificando sincronización en segundo plano...",
            variant: "warning",
          },
          res: {} as any,
          req: {} as any,
          isOffline: true,
          needsVerification: true,
          offlineId: offlineId!,
          processingTime: Date.now() - startTime,
        };

        resolve(timeoutResponse);
      }, timeoutDuration);

      let messageCount = 0;

      // Escuchar la respuesta del Service Worker
      messageChannel.port1.onmessage = async (event) => {
        messageCount++;
        clearTimeout(timeout);
        const processingTime = Date.now() - startTime;

        console.log(
          `📬 Respuesta del SW (msg #${messageCount}) para ${offlineId}:`,
          event.data
        );
  

        if (event.data.error) {
          console.error(`❌ Error del SW para ${offlineId}:`, event.data.error);
          
          if (
            event.data.error.includes(
              "no está configurada para trabajo offline"
            )
          ) {
            reject(
              new Error(
                "Esta operación no está disponible para trabajo offline"
              )
            );
          } else if (
            event.data.error.includes("Token") ||
            event.data.error.includes("401")
          ) {
            reject(
              new Error(
                "Error de autenticación. Por favor inicia sesión nuevamente."
              )
            );
          } else {
            console.warn(
              `⚠️ Error del SW pero intentando continuar: ${event.data.error}`
            );

            const errorResponse: EnhancedServiceResponseI<ObjPostI> = {
              data: {
                id: parseInt(timestamp.toString().slice(-8)),
                alert:
                  "Error en Service Worker pero datos guardados localmente. Se reintentará automáticamente.",
                variant: "warning",
              },
              res: {} as any,
              req: {} as any,
              isOffline: true,
              hasError: true,
              errorDetails: event.data.error,
              offlineId: offlineId!,
              processingTime,
            };

            resolve(errorResponse);
          }
        } else {
          console.log(
            `✅ Datos encolados exitosamente en SW para ${offlineId}`
          );
          // await logProcessingStep(
          //   offlineId!,
          //   "SW_ENQUEUE",
          //   "Datos encolados en Service Worker",
          //   "success"
          // );

          // ✅ AQUÍ ES DONDE FALTABA EL TRACKING PRINCIPAL
          // await trackServerResponseDirect(
          //   offlineId!,
          //   url,
          //   "POST",
          //   getForms,
          //   event.data, // La respuesta del SW
          //   navigator.onLine ? "sw-sync" : "offline", // Determinar el source correcto
          //   processingTime
          // );

          // Determinar el mensaje basado en el estado y la respuesta del SW
          let alertMessage = "";
          let variant: "success" | "info" | "warning" = "info";

          if (event.data.status === "enqueued") {
            alertMessage = navigator.onLine
              ? "Datos encolados, sincronizando en segundo plano..."
              : "Datos guardados offline. Se sincronizarán cuando haya conexión.";
            variant = navigator.onLine ? "info" : "warning";
            // await logProcessingStep(
            //   offlineId!,
            //   "ENQUEUE_SUCCESS",
            //   `Encolado: ${event.data.status}`,
            //   "success"
            // );
          } else if (event.data.status === "enqueued-backup") {
            alertMessage =
              "Datos guardados en sistema alternativo. Se sincronizarán automáticamente.";
            variant = "warning";
            // await logProcessingStep(
            //   offlineId!,
            //   "BACKUP_ENQUEUE",
            //   "Usado sistema de respaldo",
            //   "warning"
            // );
          } else if (event.data.status === "processed") {
            alertMessage = "Datos procesados y enviados correctamente.";
            variant = "success";
            // await logProcessingStep(
            //   offlineId!,
            //   "IMMEDIATE_PROCESS",
            //   "Procesado inmediatamente",
            //   "success"
            // );
          } else {
            alertMessage = "Datos procesados por Service Worker.";
            variant = "info";
          }

          const successResponse: EnhancedServiceResponseI<ObjPostI> = {
            data: {
              id: parseInt(timestamp.toString().slice(-8)),
              alert: alertMessage,
              variant: variant,
            },
            res: {} as any,
            req: {} as any,
            isOffline: true,
            swResponse: event.data,
            offlineId: offlineId!,
            enqueuedAt: new Date().toISOString(),
            processingTime,
          };

          resolve(successResponse);
        }
      };

      console.log(
        `📤 Enviando mensaje al SW - URL: ${url}, OfflineID: ${offlineId}`
      );

      // Preparar datos mejorados para el Service Worker
      const messageData = {
        type: "ENQUEUE_POST",
        url,
        data: getForms,
        offlineId,
        headers: {
          "Content-Type": "application/json",
          Authorization: validateToken,
          "X-Offline-ID": offlineId,
          "X-Request-Source": "workflow-truck",
          "X-Timestamp": timestamp.toString(),
          "X-App-Version": process.env.REACT_APP_VERSION || "unknown",
        },
        metadata: {
          apiPath: apiPath,
          requestTime: new Date().toISOString(),
          userAgent: navigator.userAgent,
          isOnline: navigator.onLine,
          clientTimestamp: timestamp,
          formType: "truck",
          dataSize: JSON.stringify(getForms).length,
          clientInfo: {
            language: navigator.language,
            platform: navigator.platform,
            cookieEnabled: navigator.cookieEnabled,
            connectionType:
              (navigator as any).connection?.effectiveType || "unknown",
            isMobile: isMobile,
          },
        },
        options: {
          priority: "high",
          maxRetries: 5,
          retryDelay: 2000,
          timeout: 30000,
          verifyResponse: true,
          persistOnError: true,
        },
      };

      // Enviar el mensaje al Service Worker con datos enriquecidos
      try {
        navigator.serviceWorker.controller!.postMessage(messageData, [
          messageChannel.port2,
        ]);
        console.log(`📨 Mensaje enviado al SW exitosamente para ${offlineId}`);
        // logProcessingStep(
        //   offlineId!,
        //   "SW_MESSAGE_SENT",
        //   "Mensaje enviado al Service Worker",
        //   "success"
        // );
      } catch (postError) {
        clearTimeout(timeout);
        console.error(
          `❌ Error al enviar mensaje al SW para ${offlineId}:`,
          postError
        );
        // logProcessingStep(
        //   offlineId!,
        //   "SW_MESSAGE_ERROR",
        //   `Error enviando mensaje: ${(postError as Error).message}`,
        //   "error"
        // );

        // ✅ TRACKEAR ERRORES DE ENVÍO AL SW
        // (async () => {
        //   await trackServerResponseDirect(
        //     offlineId!,
        //     url,
        //     "POST",
        //     getForms,
        //     { postMessageError: (postError as Error).message },
        //     "offline",
        //     Date.now() - startTime
        //   );
        // })();

        const postErrorResponse: EnhancedServiceResponseI<ObjPostI> = {
          data: {
            id: parseInt(timestamp.toString().slice(-8)),
            alert:
              "Error al comunicarse con Service Worker. Intenta nuevamente.",
            variant: "error",
          },
          res: {} as any,
          req: {} as any,
          isOffline: true,
          postMessageError: true,
          errorDetails: (postError as Error).message,
          offlineId: offlineId!,
          processingTime: Date.now() - startTime,
        };

        resolve(postErrorResponse);
      }
    });
  } catch (err: any) {
    console.error(`❌ Error general en workflowTruckPost:`, err);

    if (offlineId) {
      // await logProcessingStep(
      //   offlineId,
      //   "GENERAL_ERROR",
      //   `Error general: ${err.message}`,
      //   "error"
      // );
      // ✅ TRACKEAR ERRORES GENERALES TAMBIÉN
      // await trackServerResponseDirect(
      //   offlineId,
      //   "api/workflow-truck",
      //   "POST",
      //   getForms,
      //   { generalError: err.message, stack: err.stack },
      //   "offline",
      //   Date.now() - startTime
      // );
    }

    // Manejar errores específicos
    if (err.message === "TOKEN INVALIDO") {
      console.error("🔐 Token inválido - redirigiendo a login");
      LogoutSystem();
      throw err;
    }

    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error("🔐 Error 401 - token expirado:", obj.statusText);
      LogoutSystem();
      throw err;
    }

    // Para cualquier otro error, devolver respuesta de error
    const criticalErrorResponse: EnhancedServiceResponseI<ObjPostI> = {
      data: {
        id: 0,
        alert: `Error crítico: ${
          err.message || "Error desconocido"
        }. Contacta al administrador.`,
        variant: "error",
      },
      res: {} as any,
      req: {} as any,
      isOffline: true,
      criticalError: true,
      errorDetails: err.message || "Error desconocido",
      stack: err.stack,
      offlineId: offlineId || `error-${Date.now()}`,
      processingTime: Date.now() - startTime,
    };

    return criticalErrorResponse;
  }
};

export const workflowTruckPut = async <T>(
  id: number,
  updateForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTruckApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTruckById = async (
  id: number
): Promise<ServiceResponseI<WorkflowTruckI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTruckApi.get<
      ServiceResponseI<WorkflowTruckI>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTruckCount = async (
  type = "hoy"
): Promise<ServiceResponseI<{ total: number }>> => {
  try {
    const params = new URLSearchParams();

    type && params.append("type", type.toString());

    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTruckApi.get<
      ServiceResponseI<{ total: number }>
    >(`/count`, { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTruckDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTruckApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const useWorkflowTrucks = ({
  sorting,
  pagination,
  globalFilter,
  range,
  rangeVal,
}: PropsWorkflowTruckI) => {
  const workflowTrucksQuery = useQuery(
    ["workflowTrucks", { pagination, sorting, globalFilter, range, rangeVal }],
    () =>
      getWorkflowTrucks({ pagination, sorting, globalFilter, range, rangeVal }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  return {
    workflowTrucksQuery,
  };
};

export const generateWfTruckPdfReportbyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    //   const validateToken = ValidateCurrentToken();
    //   if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowTruckApi.get<ServiceResponseI<any>>(
      `/generate-pdf-report/${id}`
      // { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTruckSendReportById = async <T>(
  getForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTruckApi.post<ServiceResponseI<string>>(
      `/send-report-by-workflow-id`,
      getForms,
      {
        headers: { Authorization: validateToken },
      }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};
